import {
  DashboardUser,
  UserStatus,
  CreateUserRequest,
  UpdateUserRequest,
  UpdateUserPasswordRequest,
  AssignRoleRequest,
  UserStatistics
} from '../models/user.model';

import {
  UserDTO,
  CreateUserDTO,
  UpdateUserDTO,
  UpdatePasswordDTO,
  AssignRoleDTO,
  UsersListResponseDTO,
  UserStatisticsDTO
} from '../models/user.dto';

import {getRoleDisplayName, getUnknownRoleDisplayName} from '../utils/roleDisplayMapper';

// DTO to Model Mappers

/**
 * Maps UserDTO to DashboardUser model with null-safe role handling
 *
 * This mapper handles the transformation from API response format to internal model format.
 * It includes:
 * - Safe role mapping with fallback for null role data
 * - Nested marketer_info and provider_info structure mapping
 * - Primary role extraction from user_roles array (first active role)
 * - User-friendly role display name mapping
 *
 * @param dto - The UserDTO from API response
 * @returns DashboardUser model instance
 */
export const mapUserDTOToModel = (dto: UserDTO): DashboardUser => {
  // Map marketer_info if present
  const marketerInfo = dto.marketer_info ? {
    latitude: dto.marketer_info.latitude,
    longitude: dto.marketer_info.longitude,
    lastLocationUpdate: dto.marketer_info.last_location_update
      ? new Date(dto.marketer_info.last_location_update)
      : undefined,
    territory: dto.marketer_info.territory,
    vehicleType: dto.marketer_info.vehicle_type,
    performanceRating: dto.marketer_info.performance_rating
  } : undefined;

  // Map provider_info if present
  const providerInfo = dto.provider_info ? {
    companyName: dto.provider_info.company_name,
    website: dto.provider_info.website,
    description: dto.provider_info.description,
    companyLogo: dto.provider_info.company_logo,
    taxId: dto.provider_info.tax_id,
    commissionRate: dto.provider_info.commission_rate
  } : undefined;

  // Extract the primary role from user_roles array (first active role)
  const primaryRole = dto.user_roles?.find(ur => ur.is_active)?.role || dto.role;

  return {
    id: dto.id,
    username: dto.username,
    email: dto.email,
    fullName: dto.full_name,
    phoneNumber: dto.phone_number,
    // Safe role mapping with fallback for null role data
    role: primaryRole ? {
      id: primaryRole.id,
      name: primaryRole.name,
      displayName: primaryRole.display_name || getRoleDisplayName(primaryRole.name),
      description: primaryRole.description,
      permissions: primaryRole.permissions || [],
      createdAt: primaryRole.created_at,
      updatedAt: primaryRole.updated_at
    } : {
      // Fallback role when role data is null
      id: dto.role_id || 0,
      name: 'Unknown Role',
      displayName: getUnknownRoleDisplayName(),
      description: '',
      permissions: [],
      createdAt: dto.created_at,
      updatedAt: dto.updated_at
    },
    status: dto.status,
    createdAt: dto.created_at,
    updatedAt: dto.updated_at,
    deletedAt: dto.deleted_at,
    // Legacy location fields (prioritize nested info, fallback to flat fields)
    latitude: dto.marketer_info?.latitude || dto.latitude,
    longitude: dto.marketer_info?.longitude || dto.longitude,
    geoLocation: dto.geo_location,
    lastLocationUpdate: dto.marketer_info?.last_location_update || dto.last_location_update,
    // New nested profile information
    marketerInfo,
    providerInfo
  };
};

/**
 * Maps UsersListResponseDTO to internal format with pagination
 *
 * @param dto - The API response DTO containing users list and pagination
 * @returns Object containing mapped users array and pagination info
 */
export const mapUsersListResponseToModel = (dto: UsersListResponseDTO): { users: DashboardUser[]; pagination: any } => {
  return {
    users: dto.data.map(mapUserDTOToModel),
    pagination: {
      page: dto.pagination.page,
      limit: dto.pagination.limit,
      total: dto.pagination.total,
      totalPages: dto.pagination.totalPages
    }
  };
};

/**
 * Maps UserStatisticsDTO to UserStatistics model
 *
 * @param dto - The API response DTO containing user statistics
 * @returns UserStatistics model instance
 */
export const mapUserStatisticsDTOToModel = (dto: UserStatisticsDTO): UserStatistics => {
  return {
    totalUsers: dto.total,
    activeUsers: dto.active,
    inactiveUsers: dto.inactive,
    suspendedUsers: 0, // Not provided in DTO
    pendingUsers: 0, // Not provided in DTO
    marketDevelopers: dto.marketDevelopers,
    activePercentage: dto.activePercentage,
    usersByRole: dto.roleDistribution.map(rd => ({
      roleId: 0, // Not provided in DTO
      roleName: rd.role,
      count: rd.count
    })),
    recentRegistrations: [] // Not provided in DTO, empty array as fallback
  };
};

// Model to DTO Mappers
export const mapCreateUserRequestToDTO = (request: CreateUserRequest): CreateUserDTO => {
  return {
    username: request.username,
    email: request.email,
    password: request.password,
    full_name: request.fullName,
    phone_number: request.phoneNumber,
    role_id: request.roleId,
    status: request.status || UserStatus.ACTIVE
  };
};

export const mapUpdateUserRequestToDTO = (request: UpdateUserRequest): UpdateUserDTO => {
  const dto: UpdateUserDTO = {};

  if (request.username !== undefined) {dto.username = request.username;}
  if (request.email !== undefined) {dto.email = request.email;}
  if (request.fullName !== undefined) {dto.full_name = request.fullName;}
  if (request.phoneNumber !== undefined) {dto.phone_number = request.phoneNumber;}
  if (request.roleId !== undefined) {dto.role_id = request.roleId;}
  if (request.status !== undefined) {dto.status = request.status;}

  return dto;
};

export const mapUpdatePasswordRequestToDTO = (request: UpdateUserPasswordRequest): UpdatePasswordDTO => {
  return {
    newPassword: request.newPassword
  };
};

export const mapAssignRoleRequestToDTO = (request: AssignRoleRequest): AssignRoleDTO => {
  return {
    role_id: request.roleId,
    assigned_by: request.assignedBy || 1 // Default to admin user if not provided
  };
};
